import React, { useState, useRef, useEffect } from 'react';
import IconButton from './IconButton';
import DropdownMenu from './ui/DropdownMenu';
import { IconName } from './icons/Icon';
import { TooltipPosition } from './Tooltip';

interface MenuItemConfig {
  type?: 'Default' | 'Destructive';
  label: string;
  leftIcon?: IconName;
  rightIcon?: IconName;
  showLeftIcon?: boolean;
  showRightIcon?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

interface MenuButtonProps {
  /** アイコンボタンのアイコン */
  icon: IconName;
  /** アイコンボタンのツールチップ */
  tooltip?: string;
  /** ツールチップの位置 */
  tooltipPosition?: TooltipPosition;
  /** アイコンボタンのaria-label */
  'aria-label'?: string;
  /** アイコンボタンのタイプ */
  type?: 'primary' | 'ghost';
  /** アイコンボタンの無効状態 */
  disabled?: boolean;
  /** アイコンボタンのクラス名 */
  className?: string;
  /** ドロップダウンメニューの項目 */
  items: MenuItemConfig[];
  /** ドロップダウンメニューの位置調整用スタイル */
  menuStyle?: React.CSSProperties;
  /** ドロップダウンメニューの幅 */
  menuWidth?: string | number;
  /** メニューボタンがクリックされた時に親のクリックイベントを阻止するかどうか */
  stopPropagation?: boolean;
}

const MenuButton: React.FC<MenuButtonProps> = ({
  icon,
  tooltip,
  tooltipPosition = 'top-right',
  'aria-label': ariaLabel,
  type = 'ghost',
  disabled = false,
  className = '',
  items,
  menuStyle = {},
  menuWidth = '180px',
  stopPropagation = true
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // メニューの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  // ESCキーでメニューを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isMenuOpen && event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isMenuOpen]);

  // メニューボタンクリック処理
  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // コンテナのクリック処理（親のクリックイベントを阻止）
  const handleContainerClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  // メニュー項目クリック時にメニューを閉じる処理を追加
  const wrappedItems: MenuItemConfig[] = items.map(item => ({
    ...item,
    onClick: () => {
      item.onClick?.();
      setIsMenuOpen(false);
    }
  }));

  const defaultMenuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    width: menuWidth,
    zIndex: 1080,
    ...menuStyle
  };

  return (
    <div 
      ref={menuRef} 
      style={{ position: 'relative' }} 
      onClick={handleContainerClick}
    >
      <IconButton
        icon={icon}
        onClick={handleMenuClick}
        tooltip={isMenuOpen ? undefined : tooltip}
        tooltipPosition={tooltipPosition}
        aria-label={ariaLabel}
        type={type}
        disabled={disabled}
        className={className}
      />
      {isMenuOpen && (
        <div style={defaultMenuStyle}>
          <DropdownMenu items={wrappedItems} />
        </div>
      )}
    </div>
  );
};

export default MenuButton;